import { ofType, StateObservable } from "redux-observable";
import { Observable, of } from 'rxjs';
import { ajax } from "rxjs/ajax";
import {  filter, map, catchError, exhaustMap, mergeMap } from "rxjs/operators";
import {ErrorAction, ReduxAction} from "../../models/common/ActionTypes";
import {AppState} from "../../reducers/RootReducer";
import {
    TimesheetTaskPredefinedDescription, TimesheetTaskPredefinedDescriptionId,
    TimesheetTaskPredefinedDescriptionModule, TimesheetTaskPredefinedDescriptionProp
} from "../../models/timesheet/TimesheetTaskPredefinedDescription";
import { TimesheetTaskId } from "../../models/timesheet/TimesheetTask";
import {ViewIdentifiable} from "../../models/common/ViewSettings";
import {Identifier} from "../../models/common/Identifier";
import {APP_CONFIG} from "../../config/GeneralConfig";
import {ApiReturnType, getUserViewSettingsApiRequest} from "../../models/common/ApiReturnType";
import {eventNotificationError, eventNotificationSuccess} from "../common/EventNotificationActions";
import {RouteConfig, RouteMethods} from "../../config/RouteConfig";
import {sendUserToPage} from "../common/UserViewSettingsActions";


export type TimesheetTaskPredefDescriptionActionTypes = FetchTimesheetTaskPredefDescriptionActionTypes
    | DeleteTimesheetTaskPredefDescriptionActionTypes
    | UpdateTimesheetTaskPredefDescriptionActionTypes
    | CreateTimesheetTaskPredefDescriptionActionTypes
    | RestoreTimesheetTaskPredefDescriptionActionTypes
    | InitFormTimesheetTaskPredefDescriptionAction

/**
 * base path for all timesheet task pre defined descriptions
 *
 * @param taskId timesheet task identifier
 */
function basePath( taskId: number ): string {
    return `${ APP_CONFIG.baseWeb }timesheet/tasks/${ taskId }/descriptions/`;
}

function specificEntryPath( taskId: Identifier<number>, predefId: Identifier<number> ): string {
    return `${ basePath( taskId.rawValue ) }${ predefId.rawValue }`;
}

/**
 * Fetch TimesheetTaskPredefDescription
 */
// typescript types for fetch redux/redux-observable action
export interface FetchTimesheetTaskPredefDescriptionAction extends TimesheetTaskId, ViewIdentifiable, ReduxAction<typeof FETCH_TASK_PREDEF_DESCRIPTION > {
}
export interface FetchTimesheetTaskPredefDescriptionSuccessAction extends TimesheetTaskId, ViewIdentifiable, ReduxAction<typeof FETCH_TASK_PREDEF_DESCRIPTION_SUCCESS> {
    descriptions: ApiReturnType< TimesheetTaskPredefinedDescription[] >
}
export interface FetchTimesheetTaskPredefDescriptionErrorAction extends TimesheetTaskId, ViewIdentifiable, ErrorAction<typeof FETCH_TASK_PREDEF_DESCRIPTION_ERROR> {
}

export type FetchTimesheetTaskPredefDescriptionActionTypes = FetchTimesheetTaskPredefDescriptionAction
    | FetchTimesheetTaskPredefDescriptionSuccessAction
    | FetchTimesheetTaskPredefDescriptionErrorAction

// redux type constants for fetch
export const FETCH_TASK_PREDEF_DESCRIPTION = "FETCH_TASK_PREDEF_DESCRIPTION";
export const FETCH_TASK_PREDEF_DESCRIPTION_SUCCESS = "FETCH_TASK_PREDEF_DESCRIPTION_SUCCESS";
export const FETCH_TASK_PREDEF_DESCRIPTION_ERROR = "FETCH_TASK_PREDEF_DESCRIPTION_ERROR";

// redux functions for fetch
export function fetchTimesheetTaskPredefDescription( viewIdentifier: string, taskId: Identifier<number> ): FetchTimesheetTaskPredefDescriptionAction {
    return { type: FETCH_TASK_PREDEF_DESCRIPTION, timesheetTaskId: taskId, viewIdentifier };
}
export function fetchTimesheetTaskPredefDescriptionSuccess(
    viewIdentifier: string,
    taskId: Identifier<number>,
    descriptions: ApiReturnType< TimesheetTaskPredefinedDescription[] >
): FetchTimesheetTaskPredefDescriptionSuccessAction {
    return {
        type: FETCH_TASK_PREDEF_DESCRIPTION_SUCCESS,
        timesheetTaskId: taskId,
        descriptions,
        viewIdentifier
    };
}
export function fetchTimesheetTaskPredefDescriptionError(
    viewIdentifier: string,
    taskId: Identifier<number>,
    error: any
): FetchTimesheetTaskPredefDescriptionErrorAction {
    return {
        type: FETCH_TASK_PREDEF_DESCRIPTION_ERROR,
        error,
        timesheetTaskId: taskId,
        viewIdentifier
    };
}
// redux observable fetch epic
export function fetchTimesheetTaskPredefDescriptionEpic(
    action$: Observable< FetchTimesheetTaskPredefDescriptionAction >,
    store$: StateObservable<AppState>
) {
    return action$.pipe(
        ofType( FETCH_TASK_PREDEF_DESCRIPTION ),
        filter( action => action.viewIdentifier === TimesheetTaskPredefinedDescriptionModule.identifier ),
        exhaustMap( action => {
            const req = getUserViewSettingsApiRequest( basePath( action.timesheetTaskId.rawValue ), action.viewIdentifier, store$  );

            return ajax( APP_CONFIG.getRequestObject( req.path ) ).pipe(
                map( response => fetchTimesheetTaskPredefDescriptionSuccess(
                    action.viewIdentifier,
                    action.timesheetTaskId,
                    response.response as ApiReturnType< TimesheetTaskPredefinedDescription[] >
                ) ),
                catchError( error => of(
                    fetchTimesheetTaskPredefDescriptionError( action.viewIdentifier, action.timesheetTaskId, error ),
                    eventNotificationError( error, store$, TimesheetTaskPredefinedDescriptionModule.identifier, "fetchTimesheetTaskPredefDescription" )
                ) )
            );
        })
    );
}

/**
 * Create TimesheetTaskPredefDescription
 */
//typescript types for create redux / redux-observable actions
export interface CreateTimesheetTaskPredefDescriptionAction extends TimesheetTaskId, TimesheetTaskPredefinedDescriptionProp, ReduxAction<typeof CREATE_TASK_PREDEF_DESCRIPTION> {}
export interface CreateTimesheetTaskPredefDescriptionSuccessAction extends TimesheetTaskId, TimesheetTaskPredefinedDescriptionProp, ReduxAction<typeof CREATE_TASK_PREDEF_DESCRIPTION_SUCCESS> {}
export interface CreateTimesheetTaskPredefDescriptionErrorAction extends TimesheetTaskId, ErrorAction< typeof CREATE_TASK_PREDEF_DESCRIPTION_ERROR> {}

export type CreateTimesheetTaskPredefDescriptionActionTypes = CreateTimesheetTaskPredefDescriptionAction
    | CreateTimesheetTaskPredefDescriptionSuccessAction
    | CreateTimesheetTaskPredefDescriptionErrorAction

// redux type constants for create
export const CREATE_TASK_PREDEF_DESCRIPTION = "CREATE_TASK_PREDEF_DESCRIPTION";
export const CREATE_TASK_PREDEF_DESCRIPTION_SUCCESS = "CREATE_TASK_PREDEF_DESCRIPTION_SUCCESS";
export const CREATE_TASK_PREDEF_DESCRIPTION_ERROR = "CREATE_TASK_PREDEF_DESCRIPTION_ERROR";

//redux action functions for create
export function createTimesheetTaskPredefDescription(
    taskId: Identifier<number>,
    preDefDescription: TimesheetTaskPredefinedDescription
): CreateTimesheetTaskPredefDescriptionAction {
    return { type: CREATE_TASK_PREDEF_DESCRIPTION, timesheetTaskId: taskId, preDefDescription };
}
export function createTimesheetTaskPredefDescriptionSuccess(
    taskId: Identifier<number>,
    preDefDescription: TimesheetTaskPredefinedDescription
): CreateTimesheetTaskPredefDescriptionSuccessAction {
    return { type: CREATE_TASK_PREDEF_DESCRIPTION_SUCCESS, timesheetTaskId: taskId, preDefDescription };
}
export function createTimesheetTaskPredefDescriptionError( taskId: Identifier<number>, error: any ): CreateTimesheetTaskPredefDescriptionErrorAction {
    return { type: CREATE_TASK_PREDEF_DESCRIPTION_ERROR, timesheetTaskId: taskId, error: error };
}

//redux observable epic for create
export function createTimesheetTaskPredefDescriptionEpic(
    action$: Observable< CreateTimesheetTaskPredefDescriptionAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( CREATE_TASK_PREDEF_DESCRIPTION ),
        exhaustMap( action => {
            const pathTask = RouteConfig.timesheetTasks.routes.detail.link;

            return ajax( APP_CONFIG.getRequestObject(
                        basePath( action.timesheetTaskId.rawValue ),
                        RouteMethods.post,
                        action.preDefDescription
                    )
                ).pipe(
                    mergeMap( response => of(
                        createTimesheetTaskPredefDescriptionSuccess(
                            action.timesheetTaskId,
                            response.response as TimesheetTaskPredefinedDescription
                        ),
                        fetchTimesheetTaskPredefDescription( TimesheetTaskPredefinedDescriptionModule.identifier, action.timesheetTaskId ),
                        sendUserToPage(
                            pathTask ?  pathTask( action.timesheetTaskId.rawValue ) : '/timesheet_tasks/',
                            TimesheetTaskPredefinedDescriptionModule.identifier ),
                        eventNotificationSuccess()
                    ) ),
                    catchError( error => of(
                        createTimesheetTaskPredefDescriptionError( action.timesheetTaskId, error ),
                        eventNotificationError( error, store$, TimesheetTaskPredefinedDescriptionModule.identifier, "createTimesheetTaskPredefDescription" )
                    ) )
                )
            }
        )
    );
}

/**
 * DELETE TimesheetTaskPredefDescription
 */
//typescript redux action types
export interface DeleteTimesheetTaskPredefDescriptionAction extends TimesheetTaskId, TimesheetTaskPredefinedDescriptionId, ReduxAction<typeof DELETE_TASK_PREDEF_DESCRIPTION> {}
export interface DeleteTimesheetTaskPredefDescriptionSuccessAction extends TimesheetTaskId, TimesheetTaskPredefinedDescriptionId, ReduxAction<typeof DELETE_TASK_PREDEF_DESCRIPTION_SUCCESS> {}
export interface DeleteTimesheetTaskPredefDescriptionErrorAction extends TimesheetTaskId, TimesheetTaskPredefinedDescriptionId, ErrorAction<typeof DELETE_TASK_PREDEF_DESCRIPTION_ERROR> {}

export type DeleteTimesheetTaskPredefDescriptionActionTypes = DeleteTimesheetTaskPredefDescriptionAction
    | DeleteTimesheetTaskPredefDescriptionErrorAction
    | DeleteTimesheetTaskPredefDescriptionSuccessAction

//redux type constants
export const DELETE_TASK_PREDEF_DESCRIPTION = "DELETE_PREDEF_DESCRIPTION";
export const DELETE_TASK_PREDEF_DESCRIPTION_SUCCESS = "DELETE_PREDEF_DESCRIPTION_SUCCESS";
export const DELETE_TASK_PREDEF_DESCRIPTION_ERROR = "DELETE_PREDEF_DESCRIPTION_ERROR";

//redux action functions
export function deleteTimesheetTaskPredefDescription(taskId: Identifier<number>, taskDescriptionId: Identifier<number> ): DeleteTimesheetTaskPredefDescriptionAction {
    return { type: DELETE_TASK_PREDEF_DESCRIPTION, taskDescriptionId, timesheetTaskId: taskId };
}
export function deleteTimesheetTaskPredefDescriptionSuccess(taskId: Identifier<number>, taskDescriptionId: Identifier<number> ): DeleteTimesheetTaskPredefDescriptionSuccessAction {
    return { type: DELETE_TASK_PREDEF_DESCRIPTION_SUCCESS, taskDescriptionId, timesheetTaskId: taskId};
}
export function deleteTimesheetTaskPredefDescriptionError(taskId: Identifier<number>, taskDescriptionId: Identifier<number>, error: any ): DeleteTimesheetTaskPredefDescriptionErrorAction {
    return { type: DELETE_TASK_PREDEF_DESCRIPTION_ERROR, taskDescriptionId, error,  timesheetTaskId: taskId };
}

//redux-observable epic for delete
export function deleteTimesheetTaskPredefDescriptionEpic(
    action$: Observable< DeleteTimesheetTaskPredefDescriptionAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( DELETE_TASK_PREDEF_DESCRIPTION ),
        exhaustMap( action => ajax(
            APP_CONFIG.getRequestObject(
                specificEntryPath( action.timesheetTaskId, action.taskDescriptionId ),
                RouteMethods.delete
            )
        ).pipe(
            mergeMap( _ => of(
                deleteTimesheetTaskPredefDescriptionSuccess( action.timesheetTaskId, action.taskDescriptionId ),
                eventNotificationSuccess()
            )),
            catchError( error => of(
                deleteTimesheetTaskPredefDescriptionError( action.timesheetTaskId, action.taskDescriptionId, error ) ,
                eventNotificationError( error, store$, TimesheetTaskPredefinedDescriptionModule.identifier, "deleteTimesheetTaskPredefDescription" )
            ) )
        ) )
    );
}

/**
 * UPDATE TimesheetTaskPredefDescription
 */
// typescript redux action types
export interface UpdateTimesheetTaskPredefDescriptionAction extends TimesheetTaskId, TimesheetTaskPredefinedDescriptionProp, ReduxAction<typeof UPDATE_TASK_PREDEF_DESCRIPTION> {}
export interface UpdateTimesheetTaskPredefDescriptionSuccessAction extends TimesheetTaskId, TimesheetTaskPredefinedDescriptionProp, ReduxAction<typeof UPDATE_TASK_PREDEF_DESCRIPTION_SUCCESS> {}
export interface UpdateTimesheetTaskPredefDescriptionErrorAction extends TimesheetTaskId, ErrorAction<typeof UPDATE_TASK_PREDEF_DESCRIPTION_ERROR> {}

export type UpdateTimesheetTaskPredefDescriptionActionTypes = UpdateTimesheetTaskPredefDescriptionAction
    | UpdateTimesheetTaskPredefDescriptionSuccessAction
    | UpdateTimesheetTaskPredefDescriptionErrorAction

//redux action types
export const UPDATE_TASK_PREDEF_DESCRIPTION = "UPDATE_TASK_PREDEF_DESCRIPTION";
export const UPDATE_TASK_PREDEF_DESCRIPTION_SUCCESS = "UPDATE_TASK_PREDEF_DESCRIPTION_SUCCESS";
export const UPDATE_TASK_PREDEF_DESCRIPTION_ERROR = "UPDATE_TASK_PREDEF_DESCRIPTION_ERROR";

//redux action functions
export function updateTimesheetTaskPredefDescription(taskId: Identifier<number>, description: TimesheetTaskPredefinedDescription ): UpdateTimesheetTaskPredefDescriptionAction {
    return { type: UPDATE_TASK_PREDEF_DESCRIPTION, timesheetTaskId: taskId, preDefDescription: description };
}
export function updateTimesheetTaskPredefDescriptionSuccess(taskId: Identifier<number>, description: TimesheetTaskPredefinedDescription ): UpdateTimesheetTaskPredefDescriptionSuccessAction {
    return { type: UPDATE_TASK_PREDEF_DESCRIPTION_SUCCESS, timesheetTaskId: taskId, preDefDescription: description };
}
export function updateTimesheetTaskPredefDescriptionError(taskId: Identifier<number>, error: any): UpdateTimesheetTaskPredefDescriptionErrorAction {
    return { type: UPDATE_TASK_PREDEF_DESCRIPTION_ERROR, timesheetTaskId: taskId, error };
}

//redux observable epic for update
export function updateTimesheetTaskPredefDescriptionEpic(
    action$: Observable< UpdateTimesheetTaskPredefDescriptionAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( UPDATE_TASK_PREDEF_DESCRIPTION ),
        exhaustMap( action => {
            const pathTask = RouteConfig.timesheetTasks.routes.detail.link;
            return ajax(
                    APP_CONFIG.getRequestObject(
                        specificEntryPath( action.timesheetTaskId, action.preDefDescription.taskDescriptionId ),
                        RouteMethods.put,
                        action.preDefDescription
                    )
                ).pipe(
                    mergeMap( response => of(
                        updateTimesheetTaskPredefDescriptionSuccess(
                            action.timesheetTaskId,
                            response.response as TimesheetTaskPredefinedDescription
                        ),
                        sendUserToPage(
                            pathTask ?  pathTask( action.timesheetTaskId.rawValue ) : '/timesheet_tasks/',
                            TimesheetTaskPredefinedDescriptionModule.identifier ),
                        eventNotificationSuccess()
                    ) ),
                    catchError( error => of(
                        updateTimesheetTaskPredefDescriptionError( action.timesheetTaskId, error ),
                        eventNotificationError( error, store$, TimesheetTaskPredefinedDescriptionModule.identifier, "updateTimesheetTaskPredefDescription" )
                    ) )
                )
            }
        )
    );
}

/**
 * RESTORE TimesheetTaskPredefDescription
 */
//typescript redux action types
export interface RestoreTimesheetTaskPredefDescriptionAction extends TimesheetTaskId, TimesheetTaskPredefinedDescriptionId, ReduxAction<typeof RESTORE_TASK_PREDEF_DESCRIPTION> {}
export interface RestoreTimesheetTaskPredefDescriptionSuccessAction extends TimesheetTaskId, TimesheetTaskPredefinedDescriptionId, ReduxAction<typeof RESTORE_TASK_PREDEF_DESCRIPTION_SUCCESS> {}
export interface RestoreTimesheetTaskPredefDescriptionErrorAction extends TimesheetTaskId, TimesheetTaskPredefinedDescriptionId, ErrorAction<typeof RESTORE_TASK_PREDEF_DESCRIPTION_ERROR> {}

export type RestoreTimesheetTaskPredefDescriptionActionTypes = RestoreTimesheetTaskPredefDescriptionAction
    | RestoreTimesheetTaskPredefDescriptionSuccessAction
    | RestoreTimesheetTaskPredefDescriptionErrorAction

//redux action type constants
export const RESTORE_TASK_PREDEF_DESCRIPTION = "RESTORE_TASK_PREDEF_DESCRIPTION";
export const RESTORE_TASK_PREDEF_DESCRIPTION_SUCCESS = "RESTORE_TASK_PREDEF_DESCRIPTION_SUCCESS";
export const RESTORE_TASK_PREDEF_DESCRIPTION_ERROR = "RESTORE_TASK_PREDEF_DESCRIPTION_ERROR";

//redux action functions
export function restoreTimesheetTaskPredefDescription( taskId: Identifier<number>, predefId: Identifier<number> ): RestoreTimesheetTaskPredefDescriptionAction {
    return { type: RESTORE_TASK_PREDEF_DESCRIPTION, timesheetTaskId: taskId, taskDescriptionId: predefId };
}
export function restoreTimesheetTaskPredefDescriptionSuccess( taskId: Identifier<number>, predefId: Identifier<number> ): RestoreTimesheetTaskPredefDescriptionSuccessAction {
    return { type: RESTORE_TASK_PREDEF_DESCRIPTION_SUCCESS, timesheetTaskId: taskId, taskDescriptionId: predefId  };
}
export function restoreTimesheetTaskPredefDescriptionError( taskId: Identifier<number>, predefId: Identifier<number>, error: any ): RestoreTimesheetTaskPredefDescriptionErrorAction {
    return { type: RESTORE_TASK_PREDEF_DESCRIPTION_ERROR, timesheetTaskId: taskId, taskDescriptionId: predefId, error  };
}

//redux observable epic for restore
export function restoreTimesheetTaskPredefDescriptionEpic(
    action$: Observable< RestoreTimesheetTaskPredefDescriptionAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( RESTORE_TASK_PREDEF_DESCRIPTION ),
        exhaustMap( action => ajax(
            APP_CONFIG.getRequestObject(
                specificEntryPath( action.timesheetTaskId, action.taskDescriptionId ),
                RouteMethods.patch
            )
        ).pipe(
            mergeMap( _ => of(
                restoreTimesheetTaskPredefDescriptionSuccess( action.timesheetTaskId, action.taskDescriptionId ),
                eventNotificationSuccess()
            ) ),
            catchError( error => of(
                restoreTimesheetTaskPredefDescriptionError( action.timesheetTaskId, action.taskDescriptionId, error ),
                eventNotificationError( error, store$, TimesheetTaskPredefinedDescriptionModule.identifier, "restoreTimesheetTaskPredefDescription" )
            ) )
        ) )
    );
}

/**
 * INIT PREDEFINED DESCRIPTION FORM
 */
export interface InitFormTimesheetTaskPredefDescriptionAction extends ReduxAction<typeof INIT_FORM_TASK_PREDEF_DESCRIPTION> {
    description: TimesheetTaskPredefinedDescription
}

export const INIT_FORM_TASK_PREDEF_DESCRIPTION = "INIT_FORM_TASK_PREDEF_DESCRIPTION";

export function initFormTimesheetTaskPredefDescription(description: TimesheetTaskPredefinedDescription ): InitFormTimesheetTaskPredefDescriptionAction {
    return { type: INIT_FORM_TASK_PREDEF_DESCRIPTION, description };
}